import { createMemo, createSignal, For, Show } from "solid-js";
import { createQuery } from "@tanstack/solid-query";
import { Tag } from "./ui/index";

type BugStatus = "todo" | "triage" | "llm_ready" | "processing" | "done" | "rejected";

interface BugReport {
  id: string;
  status: BugStatus;
  severity: string;
  summary: string;
  category: string;
  session_code: string | null;
  host_id: string | null;
  created_at: string;
}

interface ListResponse {
  ok: boolean;
  reports: BugReport[];
}

async function jget<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: "include" });
  if (!r.ok) throw new Error(`${url} → ${r.status}`);
  return r.json() as Promise<T>;
}

async function jpost(url: string, body?: unknown): Promise<void> {
  const r = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
}

async function jpatch(url: string, body: unknown): Promise<void> {
  const r = await fetch(url, {
    method: "PATCH",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
}

function variantForStatus(status: BugStatus): "ok" | "warn" | "danger" | "info" | undefined {
  if (status === "done") return "ok";
  if (status === "rejected") return "danger";
  if (status === "llm_ready" || status === "processing") return "info";
  if (status === "triage") return "warn";
  return undefined;
}

function variantForSeverity(severity: string): "ok" | "warn" | "danger" | "info" | undefined {
  if (severity === "critical" || severity === "high") return "danger";
  if (severity === "medium") return "warn";
  if (severity === "low") return "ok";
  return "info";
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

function errorMessage(error: unknown): string | null {
  if (!error) return null;
  return error instanceof Error ? error.message : String(error);
}

export function SuperAdminSignalementsPage() {
  const [statusInput, setStatusInput] = createSignal<"" | BugStatus>("");
  const [appliedStatus, setAppliedStatus] = createSignal<"" | BugStatus>("");
  const [err, setErr] = createSignal<string | null>(null);

  const listUrl = createMemo(() => {
    const params = new URLSearchParams();
    if (appliedStatus()) params.set("status", appliedStatus());
    params.set("limit", "100");
    return `/api/super-admin/signalements?${params.toString()}`;
  });

  const reportsQ = createQuery<ListResponse>(() => ({
    queryKey: ["super-admin-signalements", listUrl()],
    queryFn: () => jget<ListResponse>(listUrl()),
  }));

  function applyFilters() {
    setAppliedStatus(statusInput());
  }

  async function run(fn: () => Promise<unknown>) {
    setErr(null);
    try {
      await fn();
      reportsQ.refetch();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  function setStatus(id: string, status: BugStatus) {
    run(() => jpatch(`/api/super-admin/signalements/${id}`, { status }));
  }

  function prepareLlm(id: string) {
    run(() => jpost(`/api/super-admin/signalements/${id}/prepare-llm`));
  }

  const loadError = () => errorMessage(reportsQ.error);

  const inputStyle = {
    background: "var(--bg-2, #1e293b)",
    color: "var(--text-1, #e8ecf3)",
    border: "1px solid var(--border, #334155)",
    "border-radius": "4px",
    padding: "4px 8px",
    "font-size": "12px",
  } as const;

  return (
    <div class="plugins-console" style={{ overflow: "auto" }}>
      <div class="plugins-console-header">
        <span class="plugins-console-title">
          Signalements
          <span class="plugins-console-sub">triage global — super admin</span>
        </span>
      </div>

      <Show when={err() || loadError()}>
        <div
          style={{
            background: "rgba(220,38,38,0.12)",
            border: "1px solid var(--danger, #dc2626)",
            color: "var(--danger, #f87171)",
            padding: "6px 10px",
            "border-radius": "4px",
            "font-size": "12px",
            "margin-bottom": "10px",
          }}
        >
          {err() || loadError()}
        </div>
      </Show>

      <div class="rmm-table-wrap" style={{ "margin-bottom": "10px" }}>
        <div style={{ display: "flex", gap: "6px", padding: "8px", "flex-wrap": "wrap" }}>
          <select
            style={inputStyle}
            value={statusInput()}
            onChange={(e) => setStatusInput(e.currentTarget.value as "" | BugStatus)}
          >
            <option value="">Tous</option>
            <option value="todo">À faire</option>
            <option value="triage">Triage</option>
            <option value="llm_ready">LLM prêt</option>
            <option value="processing">Traitement</option>
            <option value="done">Terminé</option>
            <option value="rejected">Rejeté</option>
          </select>
          <button class="btn btn--xs btn--info" type="button" onClick={applyFilters}>
            Appliquer
          </button>
        </div>
      </div>

      <div class="rmm-table-wrap">
        <table class="rmm-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Statut</th>
              <th>Sévérité</th>
              <th>Résumé</th>
              <th>Session</th>
              <th>Host</th>
              <th>Créé</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <Show
              when={!reportsQ.isLoading}
              fallback={
                <tr>
                  <td colSpan={8}>Chargement…</td>
                </tr>
              }
            >
              <For each={reportsQ.data?.reports || []}>
                {(report) => (
                  <tr>
                    <td style={{ "font-family": "var(--font-mono)", "font-size": "11px" }}>{report.id.slice(0, 8)}</td>
                    <td>
                      <Tag variant={variantForStatus(report.status)}>{report.status}</Tag>
                    </td>
                    <td>
                      <Tag variant={variantForSeverity(report.severity)}>{report.severity}</Tag>
                    </td>
                    <td title={report.summary}>{report.summary}</td>
                    <td style={{ "font-size": "11px" }}>{report.session_code || "—"}</td>
                    <td style={{ "font-size": "11px" }}>{report.host_id?.slice(0, 10) || "—"}</td>
                    <td style={{ "font-size": "11px", color: "var(--text-4)" }}>{fmtDate(report.created_at)}</td>
                    <td>
                      <select
                        style={inputStyle}
                        value={report.status}
                        onChange={(e) => setStatus(report.id, e.currentTarget.value as BugStatus)}
                      >
                        <option value="todo">todo</option>
                        <option value="triage">triage</option>
                        <option value="llm_ready">llm_ready</option>
                        <option value="processing">processing</option>
                        <option value="done">done</option>
                        <option value="rejected">rejected</option>
                      </select>
                      <button class="btn btn--xs btn--info" type="button" onClick={() => prepareLlm(report.id)}>
                        LLM
                      </button>
                    </td>
                  </tr>
                )}
              </For>
              <Show when={!reportsQ.error && (reportsQ.data?.reports || []).length === 0}>
                <tr>
                  <td colSpan={8} style={{ color: "var(--text-4)", "text-align": "center", padding: "14px" }}>
                    Aucun signalement.
                  </td>
                </tr>
              </Show>
            </Show>
          </tbody>
        </table>
      </div>
    </div>
  );
}
